import { useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  TabList,
  Tab,
  Text,
  HStack,
  Badge,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { MeetingIssueCard } from './MeetingIssueCard';
import { MeetingIssue, Issue } from '../../types';

interface MeetingIssuesListProps {
  meetingIssues: (MeetingIssue & { issue?: Issue })[];
  onResolve: (issueId: string, issueName: string) => void;
  isPresenter?: boolean;
}

type TabType = 'all' | 'metric' | 'rock' | 'company';

export function MeetingIssuesList({
  meetingIssues,
  onResolve,
  isPresenter = true,
}: MeetingIssuesListProps) {
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const filteredIssues = useMemo(() => {
    switch (activeTab) {
      case 'metric':
        return meetingIssues.filter((mi) => mi.issue?.relatedMetricId);
      case 'rock':
        return meetingIssues.filter((mi) => mi.issue?.relatedRockId);
      case 'company':
        return meetingIssues.filter((mi) => mi.issue?.isCompany);
      default:
        return meetingIssues;
    }
  }, [activeTab, meetingIssues]);

  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const cardHover = useColorModeValue('gray.50', 'gray.700');

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Tabs index={['all', 'metric', 'rock', 'company'].indexOf(activeTab)} onChange={(index) => {
          const tabs: TabType[] = ['all', 'metric', 'rock', 'company'];
          setActiveTab(tabs[index]);
        }}>
          <TabList>
            <Tab onClick={() => setActiveTab('all')}>All</Tab>
            <Tab onClick={() => setActiveTab('metric')}>Metric</Tab>
            <Tab onClick={() => setActiveTab('rock')}>Rock</Tab>
            <Tab onClick={() => setActiveTab('company')}>Company</Tab>
          </TabList>
        </Tabs>

      </HStack>

      {filteredIssues.length === 0 ? (
        <Box bg={bgColor} borderRadius="md" p={12} textAlign="center">
          <Text color="gray.600">
            {activeTab === 'all'
              ? 'No issues in this meeting yet.'
              : `No ${activeTab} issues in this meeting.`}
          </Text>
        </Box>
      ) : (
        <Accordion allowToggle defaultIndex={[]}>
          {filteredIssues.map((meetingIssue) => {
            const { issue } = meetingIssue;
            const isResolved = meetingIssue.resolvedInThisMeeting;
            return (
              <AccordionItem key={meetingIssue.id} border="none" mb={4}>
                <AccordionButton
                  px={4}
                  py={3}
                  bg={cardBg}
                  borderWidth={1}
                  borderColor={cardBorder}
                  borderRadius="md"
                  _hover={{ bg: cardHover }}
                >
                  <Box flex={1} textAlign="left">
                    <HStack spacing={2} align="center">
                      <Text fontSize="lg" fontWeight="semibold" opacity={isResolved ? 0.6 : 1}>
                        {issue?.name || 'Unknown Issue'}
                      </Text>
                      {isResolved && (
                        <Badge colorScheme="green" fontSize="xs">
                          Resolved
                        </Badge>
                      )}
                      {meetingIssue.weeksUnresolvedAtAdd >= 1 && !isResolved && (
                        <Badge colorScheme="red" fontSize="xs">
                          Week {meetingIssue.weeksUnresolvedAtAdd}
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={0} pb={4} pt={4}>
                  <MeetingIssueCard
                    meetingIssue={meetingIssue}
                    onResolve={onResolve}
                    isPresenter={isPresenter}
                  />
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

    </Box>
  );
}

